// src/components/TrendLineOverlayController.jsx

import { useLayoutEffect, useRef } from "react";
import { TrendLineManager } from "../utils/engine/TrendLineManager";
import { GlowAnimator } from "../utils/engine/GlowAnimator";
import { drawTrendLine, resetOverlays } from "../utils/chartOverlayEngine";

export default function TrendLineOverlayController({
  chartApi,
  candles,
  symbol,
  timeframe,
  settings,
}) {
  const managerRef = useRef(null);
  const animatorRef = useRef(null);
  const frameRef = useRef(null);
  const renderRef = useRef({ chartApi: null, line: null, settings: {} });

  useLayoutEffect(() => {
    if (!chartApi) return undefined;
    managerRef.current = new TrendLineManager(settings);
    renderRef.current = { chartApi, line: null, settings: settings || {} };

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      animatorRef.current?.stop();
      managerRef.current = null;
      resetOverlays(chartApi);
    };
  }, [chartApi, symbol, timeframe, settings]);

  useLayoutEffect(() => {
    if (!chartApi || !managerRef.current) return undefined;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);

    frameRef.current = requestAnimationFrame(() => {
      const result = managerRef.current.update(candles);
      const line = result.line && { ...result.line, isGlowing: result.glowing };
      renderRef.current = { chartApi, line, settings: settings || {} };

      if (result.changed) drawTrendLine(chartApi, line, settings);
      if (result.glowing) {
        if (!animatorRef.current) {
          animatorRef.current = new GlowAnimator((intensity) => {
            const current = renderRef.current;
            if (current.line?.isGlowing) drawTrendLine(current.chartApi, current.line, current.settings, intensity);
          });
        }
        animatorRef.current.start();
      } else {
        animatorRef.current?.stop();
      }
    });

    return () => frameRef.current && cancelAnimationFrame(frameRef.current);
  }, [chartApi, candles, settings]);

  return null;
}
